import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser } from '../../services/authService';
import {
  getBalance,
  getRecentTransactions,
  getUserUtxos,
  getBlockchainInfo,
} from '../../services/walletService';
import { getUserBalances } from '../../services/swapFundsService';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCubes, faInfoCircle, faChevronDown, faChevronRight,
  faCoins, faClock, faCheckCircle, faTimesCircle,
  faExchangeAlt, faArrowLeft, faArrowRight, faCube, faCheck, faTimes, faQuestionCircle, faGamepad
} from '@fortawesome/free-solid-svg-icons';
import { GiBull } from 'react-icons/gi';
import { useTranslation } from 'react-i18next';

import styles from './Dashboard.module.css';

/* --- Chip MENEL con toro --- */
const Menel = ({ value, digits = 3, tone = 'neutral', title }) => {
  const n = Number(value || 0);
  return (
    <span className={`${styles.menel} ${styles[`menel--${tone}`]}`} title={title ?? `${n.toFixed(digits)} MENEL`}>
      <span className={styles.menelValue}>{n.toFixed(digits)}</span>
      <GiBull className={styles.menelIcon} aria-hidden />
      <span className={styles.menelUnit}>MENEL</span>
    </span>
  );
};

const Dashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const shorten = (h = '') => (h?.length > 16 ? `${h.slice(0, 8)}…${h.slice(-8)}` : h || '');

  const [walletBalance, setWalletBalance] = useState({ availableBalance: 0, pendingBalance: 0, totalBalance: 0 });
  const [gameBalance, setGameBalance] = useState({ freeBalance: 0, balanceInGames: 0, totalBalance: 0 });
  const [transactions, setTransactions] = useState([]);
  const [utxos, setUtxos] = useState([]);
  const [blockchainInfo, setBlockchainInfo] = useState(null);

  const [loading, setLoading] = useState(true);
  const [openChain, setOpenChain] = useState(false);
  const [infoOpen, setInfoOpen] = useState({ tx: false, utxo: false });

  /* --- DATA: saldos, tx, utxos --- */
  const fetchData = useCallback(async () => {
    try {
      const user = getCurrentUser();
      if (!user) return navigate('/login');

      const [wb, gb, txs, uxs] = await Promise.all([
        getBalance(),
        getUserBalances(),
        getRecentTransactions(),
        getUserUtxos(),
      ]);

      setWalletBalance({
        availableBalance: wb.availableBalance ?? 0,
        pendingBalance: wb.pendingBalance ?? 0,
        totalBalance: wb.totalBalance ?? 0,
      });
      setGameBalance({
        freeBalance: gb.freeBalance ?? 0,
        balanceInGames: gb.balanceInGames ?? 0,
        totalBalance: gb.totalBalance ?? 0,
      });
      setTransactions(txs?.data?.transactions || []);
      setUtxos(uxs?.data?.utxos || []);
    } catch (e) {
      if (e?.response?.status === 401) navigate('/login');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  /* --- DATA: blockchain info --- */
  const fetchChain = useCallback(async () => {
    try {
      const res = await getBlockchainInfo();
      setBlockchainInfo(res?.data || null);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, 10000);
    return () => clearInterval(id);
  }, [fetchData]);

  const formatSize = (bytes) => {
    if (bytes >= 1073741824) return (bytes / 1073741824).toFixed(2) + ' GB';
    if (bytes >= 1048576) return (bytes / 1048576).toFixed(2) + ' MB';
    if (bytes >= 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes ?? 0) + ' bytes';
  };

  const totalCombined = Number(walletBalance.totalBalance) + Number(gameBalance.totalBalance);

  /* --- Ticker de transacciones --- */
  const [tickIdx, setTickIdx] = useState(0);
  useEffect(() => {
    if (!transactions?.length) return;
    const id = setInterval(() => setTickIdx((i) => (i + 1) % transactions.length), 1400);
    return () => clearInterval(id);
  }, [transactions]);
  const currentTx = transactions?.[tickIdx];

  return (
    <div className={styles.root}>
      {/* CABECERA BLOQUE CADENA */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.title}>
            <FontAwesomeIcon icon={faCubes} /> {t('blockchain')}
          </h2>
          <button
            className={styles.iconBtn}
            onClick={async () => {
              const willOpen = !openChain;
              setOpenChain(willOpen);
              if (willOpen && !blockchainInfo) await fetchChain();
            }}
            aria-expanded={openChain}
            aria-controls="chain-panel"
          >
            <FontAwesomeIcon icon={openChain ? faChevronDown : faChevronRight} />
          </button>
        </div>

        <div id="chain-panel" className={`${styles.collapse} ${openChain ? styles.open : ''}`}>
          <div className={styles.card}>
            {!blockchainInfo ? (
              <div className={styles.loadingInline}>
                <div className={styles.spinner} /> <span>{t('loading')}</span>
              </div>
            ) : (
              (() => {
                const rows = [
                  { label: t('chain'),        value: blockchainInfo.chain },
                  { label: t('block_height'), value: blockchainInfo.blocks?.toLocaleString() },
                  { label: t('difficulty'),   value: blockchainInfo.difficulty },
                  { label: t('best_block_hash'), value: blockchainInfo.bestblockhash },
                  { label: t('chainwork'),    value: blockchainInfo.chainwork },
                  { label: t('verification_progress'), value: `${(blockchainInfo.verificationprogress * 100).toFixed(2)}%` },
                  { label: t('median_time'),  value: (blockchainInfo.mediantime && !isNaN(blockchainInfo.mediantime))
                                                ? new Date(blockchainInfo.mediantime * 1000).toLocaleString()
                                                : t('not_available') },
                  { label: t('size_on_disk'), value: formatSize(blockchainInfo.size_on_disk) },
                  { label: t('initial_block_download'), value: blockchainInfo.initialblockdownload ? t('yes') : t('no') },
                  { label: t('warnings'),     value: blockchainInfo.warnings || t('none') },
                ];
                return (
                  <dl className={styles.kvList}>
                    {rows.map((r, i) => (
                      <div key={i} className={styles.kvItem}>
                        <dt>{r.label}</dt>
                        <dd>{r.value ?? '—'}</dd>
                      </div>
                    ))}
                  </dl>
                );
              })()
            )}
          </div>
        </div>
      </div>

      {/* TARJETAS DE SALDOS */}
      <div className={styles.cardsRow}>
        <div className={`${styles.card} ${styles.cardPrimary}`}>
          <div className={styles.cardHead}><FontAwesomeIcon icon={faCoins} /> Wallet</div>
          <div className={styles.cardBody}>
            <div className={styles.kvRow}>
              <span><FontAwesomeIcon icon={faCoins} /> Available</span>
              <Menel value={walletBalance.availableBalance} />
            </div>
            <div className={styles.kvRow}>
              <span><FontAwesomeIcon icon={faClock} /> Pending</span>
              <Menel value={walletBalance.pendingBalance} tone="warn" />
            </div>
            <div className={styles.kvRow}>
              <span><FontAwesomeIcon icon={faCheckCircle} /> Total</span>
              <Menel value={walletBalance.totalBalance} tone="ok" />
            </div>
          </div>
        </div>

        <div className={`${styles.card} ${styles.cardInfo}`}>
          <div className={styles.cardHead}><FontAwesomeIcon icon={faGamepad} /> Games</div>
          <div className={styles.cardBody}>
            <div className={styles.kvRow}>
              <span><FontAwesomeIcon icon={faGamepad} /> Free</span>
              <Menel value={gameBalance.freeBalance} />
            </div>
            <div className={styles.kvRow}>
              <span><FontAwesomeIcon icon={faGamepad} /> In Games</span>
              <Menel value={gameBalance.balanceInGames} />
            </div>
            <div className={styles.kvRow}>
              <span><FontAwesomeIcon icon={faCoins} /> Total</span>
              <Menel value={gameBalance.totalBalance} tone="ok" />
            </div>
          </div>
        </div>

        <div className={`${styles.card} ${styles.cardSuccess}`}>
          <div className={styles.cardHead}><FontAwesomeIcon icon={faCoins} /> {t('total_combined_balance')}</div>
          <div className={`${styles.cardBody} ${styles.center}`}>
            <Menel value={totalCombined} />
          </div>
        </div>
      </div>

      {/* LOADING GENERAL */}
      {loading && (
        <div className={styles.loadingBlock}>
          <div className={styles.spinner} />
          <p>{t('loading')}</p>
        </div>
      )}

      {/* TICKER */}
      {transactions?.length > 0 && (
        <div
          className={styles.tickerWrap}
          onMouseEnter={(e) => e.currentTarget.classList.add(styles.paused)}
          onMouseLeave={(e) => e.currentTarget.classList.remove(styles.paused)}
          aria-live="polite"
        >
        <div className={styles.ticker}> 
          <div
            key={currentTx?.txid ?? tickIdx}
            className={[
              styles.tickerItem,
              currentTx?.direction === 'incoming' ? styles.tiIn : styles.tiOut,
              currentTx?.status === 'pending' ? styles.tiPending : ''
            ].join(' ')}
          >
            <strong>{currentTx?.game_name || ''}</strong>
             {currentTx?.direction === 'incoming' ? `↘ ${t('received')}` : `↗ ${t('sent')}`}
             💰 {(currentTx?.amount_satoshis / 1e8).toFixed(2)} MENEL
             🕒 {new Date(currentTx?.created_at).toLocaleString()}
          </div>
        </div>
        </div>
      )}

      {/* TRANSACCIONES */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.title}><FontAwesomeIcon icon={faExchangeAlt} /> {t('recent_transactions')}</h2>
          <button
            className={styles.iconBtn}
            onClick={() => setInfoOpen(s => ({ ...s, tx: !s.tx }))}
            aria-expanded={infoOpen.tx}
            aria-controls="tx-info"
            title="Info"
          >
            <FontAwesomeIcon icon={faQuestionCircle} />
          </button>
        </div>

        {infoOpen.tx && (
          <div id="tx-info" className={styles.infoBox}>
            <p>{t('transaction_info')}</p>
            <p>{t('transaction_details')}</p>
          </div>
        )}

        <div className={styles.tableCard}>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th><FontAwesomeIcon icon={faCoins} /> {t('amount')} (MENEL)</th>
                  <th><FontAwesomeIcon icon={faCoins} /> {t('fee')}</th>
                  <th><FontAwesomeIcon icon={faExchangeAlt} /> {t('type')}</th>
                  <th><FontAwesomeIcon icon={faInfoCircle} /> {t('status')}</th>
                  <th><FontAwesomeIcon icon={faClock} /> {t('date')}</th>
                  <th><FontAwesomeIcon icon={faCube} /> TXID</th>
                </tr>
              </thead>
              <tbody>
                {transactions.length ? transactions.slice(0, 7).map((tx, i) => {
                  const amt = tx.amount_satoshis / 1e8;
                  const fee = tx.fee_satoshis / 1e8;
                  const incoming = tx.direction === 'incoming';
                  const pending = tx.status === 'pending';
                  return (
                    <tr
                      key={`${tx.txid}-${i}`}
                      className={`${styles.txRow} ${incoming ? styles.txIncoming : styles.txOutgoing} ${pending ? styles.txPending : ''}`}
                    >
                      <td><Menel value={amt} digits={8} tone={incoming ? 'ok' : 'warn'} /></td>
                      <td><Menel value={fee} digits={8} tone="warn" /></td>
                      <td>
                        {incoming ? (
                          <span className={styles.badgeOk}><FontAwesomeIcon icon={faArrowLeft} /> {t('received')}</span>
                        ) : (
                          <span className={styles.badgeWarn}><FontAwesomeIcon icon={faArrowRight} /> {t('sent')}</span>
                        )}
                      </td>
                      <td>
                        {tx.status === 'confirmed' ? (
                          <span className={styles.badgeOk}><FontAwesomeIcon icon={faCheckCircle} /> {t('confirmed')}</span>
                        ) : tx.status === 'failed' ? (
                          <span className={styles.badgeDanger}><FontAwesomeIcon icon={faTimesCircle} /> {t('failed')}</span>
                        ) : (
                          <span className={`${styles.badgeInfo} ${styles.badgePending}`}>
                            <span className={styles.spinCoin} aria-hidden>
                              <span className={styles.spinCoinFace}><GiBull /></span>
                            </span>
                            {t('pending')}
                          </span>
                        )}
                      </td>
                      <td>{new Date(tx.created_at).toLocaleString()}</td>
                      <td>
                        <a
                          className={styles.link}
                          href={`https://explorer.menelik.cc/txid/${tx.txid}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {shorten(tx.txid)}
                        </a>
                      </td>
                    </tr>
                  );
                }) : (
                  <tr><td colSpan="6" className={styles.empty}>{t('no_recent_transactions')}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* UTXOS */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.title}><FontAwesomeIcon icon={faCoins} /> {t('available_utxos')}</h2>
          <button
            className={styles.iconBtn}
            onClick={() => setInfoOpen(s => ({ ...s, utxo: !s.utxo }))}
            aria-expanded={infoOpen.utxo}
            aria-controls="utxo-info"
            title="Info"
          >
            <FontAwesomeIcon icon={faQuestionCircle} />
          </button>
        </div>

        {infoOpen.utxo && (
          <div id="utxo-info" className={styles.infoBox}>
            <p>{t('utxo_info')}</p>
          </div>
        )}

        <div className={styles.tableCard}>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Vout</th>
                  <th>{t('amount')} (MENEL)</th>
                  <th>{t('status')}</th>
                  <th>{t('date')}</th>
                  <th>TXID</th>
                </tr>
              </thead>
              <tbody>
                {utxos.length ? utxos.map((u, i) => (
                  <tr key={i}>
                    <td>{u.vout}</td>
                    <td><Menel value={u.amount_satoshis / 1e8} digits={8} /></td>
                    <td>
                      {u.is_spent
                        ? <span className={styles.badgeDanger}><FontAwesomeIcon icon={faTimes} /> {t('spent')}</span>
                        : <span className={styles.badgeOk}><FontAwesomeIcon icon={faCheck} /> {t('available')}</span>}
                    </td>
                    <td>{new Date(u.created_at).toLocaleString()}</td>
                    <td>
                      <a
                        className={styles.link}
                        href={`https://explorer.menelik.cc/txid/${u.txid}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {shorten(u.txid)}
                      </a>
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan="5" className={styles.empty}>{t('no_utxos_available')}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Dashboard;
